import * as _ from 'lodash';
import { stringify } from 'query-string';
import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';

import { orderClientType, orderType } from 'app/b2b-order/config/order';
import { MinimumOrderRestriction, Price } from 'services';

import { ApiModel } from '../../shared/models/api.model';
import { Branch } from '../../shared/models/organization.model';

@Injectable()
export class OrdersModel {
    constructor(private apiModel: ApiModel) {}

    public send({
        orderedItems,
        store,
        userData,
        retailerOrderNumber,
        additionalNotes,
        brandOptions,
        additionalConfirmationEmail
    }: SendOrderParameters): Promise<{ orderNumber: string }> {
        const url = `${this.apiModel.getOrderEndpoint()}`;
        const debitorGln = _.get(store, 'buyerAddress.gln', store.address.gln);
        const address = { ...store.address, name: store.name };
        return this.apiModel.makeHttpRequest({
            method: 'POST',
            url,
            body: {
                orderedItems,
                clientId: userData.id,
                ...(store.locationServiceId ? { storeId: store.locationServiceId } : {}),
                clientType: orderClientType,
                ...(!_.isEmpty(retailerOrderNumber) ? { retailerOrderNumber } : {}),
                debitor: {
                    email: userData.email,
                    name: userData.organizationName,
                    gln: debitorGln
                },
                ...(userData.referredRetailer
                    ? {
                          referredRetailer: {
                              organizationId: userData.referredRetailer.organizationId,
                              organizationName: userData.referredRetailer.organizationName
                          }
                      }
                    : {}),
                ...(!_.isEmpty(additionalNotes) ? { additionalNotes } : {}),
                ...(!_.isEmpty(additionalConfirmationEmail) ? { additionalConfirmationEmail } : {}),
                shippingAddress: address,
                ...(store.billingAddress ? { billingAddress: { ...address, gln: store.billingAddress.gln } } : {}),
                isTest: userData.isTest,
                brandOptions: brandOptions || [],
                type: orderType
            }
        });
    }

    public getBrandOrderSettings(brands: string[]): Observable<GetBrandOrderSettingsResponse[]> {
        const queryParams = { brands };
        return this.apiModel.makeHttpObservableRequest({
            method: 'GET',
            url: `${this.apiModel.getOrderEndpoint()}/settings?${stringify(queryParams, {
                arrayFormat: 'bracket',
                sort: false
            })}`
        });
    }
}

export enum OrderedItemType {
    Product = 'product',
    Prepack = 'prepack'
}

export interface OrderedItem {
    identifier: string;
    quantity: number | { [storeId: string]: number };
    type: OrderedItemType;
}

export interface UserData {
    id: string;
    email: string;
    organizationName: string;
    isTest?: boolean;
    referredRetailer?: {
        organizationId: string;
        organizationName: string;
    };
}

export interface SendOrderParameters {
    orderedItems?: OrderedItem[];
    store: Branch;
    userData: UserData;
    retailerOrderNumber?: string;
    additionalNotes?: string;
    additionalConfirmationEmail?: string;
    brandOptions?: BrandOrderSetting[];
}

export interface OrderItem {
    sizes: {
        name: string;
        gtin: string;
        stock: number;
        amount: number;
        price?: Price;
    }[];
    recommendedRetailPrice: number;
    discountedPurchasePrice: number;
    purchasePrice: number;
    currency: string;
    name: string;
    color: string;
    colorCode: string;
    colorName: string;
    articleNumber: string;
    image: string;
    brandId: string;
    styleGroupId: string;
    brandName: string;
    showsDefaultPricelist: boolean;
    minimumOrderRestriction: MinimumOrderRestriction;
    orderedPrepacks: StyleGroupOrderPrepack[];
}

export interface StyleGroupOrderProduct {
    gtin: string;
    brand: string;
    media: any;
    articleNumber: string;
    manufacturerAttributes: any;
    fcAttributes: any;
    colorGroupId: string;
    styleGroupId: string;
    stock: number;
    price: {
        currency: string;
        purchasePrice: number;
        discountedPurchasePrice: number;
        recommendedRetailPrice: number;
    };
    colorGroupPrice: {
        currency: string;
        purchasePrice: number;
        discountedPurchasePrice: number;
        recommendedRetailPrice: number;
    };
    showsDefaultPricelist: boolean;
    minimumOrderRestriction: MinimumOrderRestriction;
}

export interface StyleGroupOrderPrepack {
    articleNumber: string;
    brand: any;
    code: string;
    colorGroupId: string;
    colorGroupPrice: {
        currency: string;
        purchasePrice: number;
        discountedPurchasePrice: number;
        recommendedRetailPrice: number;
    };
    externalId: string;
    manufacturerAttributes: any;
    name: string;
    prepackId: string;
    styleGroupId: string;
    quantity: string;
    type: string;
}

export interface GroupedProduct {
    brandId: string;
    brandName: string;
    products: StyleGroupOrderProduct[];
}

export interface GroupedPrepack {
    brandId: string;
    brandName: string;
    prepacks: StyleGroupOrderPrepack[];
}

export interface BrandOrderSetting {
    brandId: string;
    note?: string;
    shippingOption?: string;
}

export interface GetBrandOrderSettingsResponse {
    brandId: string;
    acceptsNotes: boolean;
    shippingOptions: {
        value: string;
        label: string;
        isDefault: boolean;
    }[];
}
